import {
  daysToDeadline, entryDuration, isEntryLocked, isLive, isOverdue, isTaskOpen, linkKey, orderedTasks, taskLabel, uninvoicedByCurrency,
  type Dataset, type Invoice, type Project
} from "@otrack/domain";
import type { AssistantSource } from "./assistant";
import type { GoogleView } from "./calendar/google";
import { collectItems } from "./calendar/model";
import { projectRefs } from "./commands";
import { MINUTE } from "./time";

/**
 * Read-only lookups shared by the assistant's tools and the Fast Path, so a common question gets
 * the same records whether it was answered on the device or through the model.
 */

export type TasksSource = Extract<AssistantSource, { kind: "tasks" }>;
export type ProjectsSource = Extract<AssistantSource, { kind: "projects" }>;
export type EventsSource = Extract<AssistantSource, { kind: "events" }>;
export type UninvoicedSource = Extract<AssistantSource, { kind: "uninvoiced" }>;
export type InvoiceStatusFilter = "draft" | "sent" | "paid" | "unpaid" | "overdue";

/**
 * Live, unarchived, undelivered Projects: those closest to their Deadline first, then the most
 * recently used.
 */
function inProgress(dataset: Dataset, now: number): Project[] {
  const projects = projectRefs(dataset).map((ref) => dataset.projects[ref.id]!).filter((project) => project.completedAt === null);
  // A stable sort keeps the most recently used order among Projects without a Deadline.
  const due = (days: number | null) => days ?? Number.POSITIVE_INFINITY;
  return projects.sort((a, b) => due(daysToDeadline(a, now)) - due(daysToDeadline(b, now)));
}

/** Projects in progress, in the same order as `openTasks`, with how many open Tasks (not Sub-tasks) and time tracked. */
export function activeProjects(dataset: Dataset, now: number): ProjectsSource {
  const tracked = new Map<string, number>();
  for (const entry of Object.values(dataset.timeEntries)) {
    if (isLive(entry) && entry.projectId) tracked.set(entry.projectId, (tracked.get(entry.projectId) ?? 0) + entryDuration(entry, now));
  }
  const items = inProgress(dataset, now).map((project) => ({
    id: project.id,
    openTasks: orderedTasks(dataset, project.id, (task) => task.parentId === null && isTaskOpen(dataset, task)).length,
    tracked: tracked.get(project.id) ?? 0
  }));
  return { kind: "projects", total: items.length, items };
}

/**
 * Open Tasks in live, unarchived, undelivered Projects: the Projects closest to their Deadline
 * first, then the most recently used. Sub-tasks follow their parent.
 */
export function openTasks(dataset: Dataset, now: number, projectId?: string): TasksSource {
  const logged = new Map<string, number>();
  for (const entry of Object.values(dataset.timeEntries)) {
    if (isLive(entry) && entry.taskId) logged.set(entry.taskId, (logged.get(entry.taskId) ?? 0) + entryDuration(entry, now));
  }
  const projects = inProgress(dataset, now).filter((project) => !projectId || project.id === projectId);
  const items = projects.flatMap((project) => orderedTasks(dataset, project.id, (task) => isTaskOpen(dataset, task)).map((task) => ({
    id: task.id,
    name: taskLabel(dataset, task),
    projectId: project.id,
    logged: logged.get(task.id) ?? 0,
    estimate: task.estimateMinutes === null ? null : task.estimateMinutes * MINUTE
  })));
  return { kind: "tasks", total: items.length, items };
}

/** Planned occurrences that overlap [from, to), Google's too, in start order, with the time logged from each. */
export function plannedEvents(dataset: Dataset, from: number, to: number, now: number, google: GoogleView = NO_GOOGLE): EventsSource {
  const items = collectItems(dataset, google, from, to, now).plans.map((plan) => ({
    key: linkKey(plan.linkId, plan.linkOccurrence), title: plan.title || "Untitled", start: plan.start, end: plan.end, allDay: plan.allDay, projectId: plan.projectId,
    recurring: plan.recurring, logged: plan.logged.reduce((sum, entry) => sum + entryDuration(entry, now), 0),
    calendar: plan.google?.calendar.name ?? null
  })).sort((a, b) => a.start - b.start);
  return { kind: "events", total: items.length, items };
}

const NO_GOOGLE: GoogleView = { calendars: [], events: {} };

/** Earned money per Client that is not yet on a sent Invoice, with its hours and oldest day. */
export function uninvoicedWork(dataset: Dataset, now: number, clientId?: string): UninvoicedSource {
  const clients = Object.values(dataset.clients).filter((client) => isLive(client) && (!clientId || client.id === clientId));
  return {
    kind: "uninvoiced",
    clients: clients.flatMap((client) => {
      const money = uninvoicedByCurrency(dataset, client.id);
      if (!Object.values(money).some(Boolean)) return [];
      const entries = Object.values(dataset.timeEntries).filter((e) => isLive(e) && e.end !== null && e.billable && e.rateSnapshot && !isEntryLocked(dataset, e) && e.projectId && dataset.projects[e.projectId]?.clientId === client.id);
      const oldest = entries.length ? Math.min(...entries.map((e) => e.start)) : null;
      return [{ clientId: client.id, name: client.name, money, ms: entries.reduce((sum, e) => sum + entryDuration(e, now), 0), oldest }];
    })
  };
}

/** Live Invoices, newest first. "unpaid" means sent and not paid; "overdue", unpaid past due. */
export function findInvoices(dataset: Dataset, now: number, status?: InvoiceStatusFilter, clientId?: string): Invoice[] {
  const matches = (invoice: Invoice) => {
    switch (status) {
      case undefined: return true;
      case "unpaid": return invoice.status === "sent";
      case "overdue": return isOverdue(invoice, now);
      default: return invoice.status === status;
    }
  };
  return Object.values(dataset.invoices)
    .filter((invoice) => isLive(invoice) && (!clientId || invoice.clientId === clientId) && matches(invoice))
    .sort((a, b) => (b.issueDate ?? b.createdAt) - (a.issueDate ?? a.createdAt));
}
